<template>
  <base-popover
    v-slot="{ toggle, close, opened }"
    data-test-id="vf-dropdown"
    interactive
    :offset="-1"
    placement="bottom-start"
  >
    <base-button
      :aria-controls="opened ? id : undefined"
      :aria-expanded="opened"
      aria-haspopup="true"
      class="relative h-10 flex items-center b b-grey-10 space-x-1"
      :class="[
        brandClasses.button,
        opened ? ['z-2', brandClasses.buttonOpened, classButtonOpened] : brandClasses.buttonClosed,
        classButton,
      ]"
      :style="styleButton"
      @click="toggle"
      @keydown.esc="close"
    >
      <slot name="label">
        <span v-if="label" class="pointer-events-none text-sm">{{ label }}</span>
      </slot>
      <vf-icon class="pointer-events-none" dir="down" name="chevron" size="md" />
    </base-button>
    <base-popover-content
      :id
      class="z-1 overflow-auto rounded-xs rounded-tl-none bg-white px-4 pb-6 pt-5 shadow-lg ring-grey-70 ring-inset transition scrollbar-sm"
      :class="[brandClasses.popoverContent, classContent]"
      data-test-id="vf-dropdown-content"
      enter-active-class="transform ease-out"
      enter-from-class="op-0"
      enter-to-class="translate-y-0"
      leave-active-class="transform ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="op-0"
      style="max-width: 46.5rem; max-height: 26rem"
      :style="styleContent"
      tabindex="0"
      @keydown.esc="close"
    >
      <!-- @slot Slot for dropdown content -->
      <slot v-bind="{ selectionLimitReached, maxSelectableOptions }" />
    </base-popover-content>
  </base-popover>
</template>

<script lang="ts" setup>
import type { CSSClass } from '#types/common'

const {
  classButton,
  classButtonOpened,
  classContent,
  label,
  maxSelectableOptions,
  selectedOptionsCount,
  styleButton,
  styleContent,
} = defineProps<{
  /**
   * Label for dropdown trigger
   */
  label?: string
  /**
   * Classes to be applied to content container.
   */
  classContent?: CSSClass
  /**
   * Styles to apply to button.
   */
  classButton?: CSSClass
  /**
   * Styles to apply to button when its expanded.
   */
  classButtonOpened?: CSSClass
  /**
   * Styles to apply to content container.
   */
  styleContent?: string | object
  /**
   * Styles to apply to button.
   */
  styleButton?: string | object
  /**
   * Maximum allowed selected options for consumers using the dropdown as a multiselect.
   */
  maxSelectableOptions?: number
  /**
   * Current selected option count for consumers using the dropdown as a multiselect.
   */
  selectedOptionsCount?: number
}>()

const id = useId()
const { brandClasses } = useAppConfig().components.vf.dropdown
const selectionLimitReached = computed(() =>
  !!maxSelectableOptions
  && maxSelectableOptions > 0
  && (selectedOptionsCount || 0) >= maxSelectableOptions
)
</script>
